import { UserFactory } from "@/classes/abstractFactory";
import MessageService from "@/services/MessageService";

class Message {
  constructor(buildData) {
    this.id = buildData.id;
    this.text = buildData.text || "";
    this.date = buildData.date;
    this.userView = buildData.userView;
    this.isCurrentUser = buildData.isCurrentUser;
    this.files = buildData.files || [];
    this.isRead = buildData.isRead || false;
  }
}

class MessageBuilder {
  setId(val) {
    this.id = val;
    return this;
  }

  setText(val) {
    this.text = val;
    return this;
  }

  setDate(val) {
    this.date = val;
    return this;
  }

  setUserView(userView) {
    this.userView = userView;
    return this;
  }

  setIsCurrentUser(val) {
    this.isCurrentUser = val;
    return this;
  }

  setFiles(val) {
    this.files = val;
    return this;
  }

  setIsRead(val) {
    this.isRead = val;
    return this;
  }

  build() {
    return new Message(this);
  }
}

function createUser(sender) {
  const builder = UserFactory.getUserBuilder();
  const user = builder
    .setFirstName(sender.first_name)
    .setLastName(sender.last_name)
    .setEmail(sender.email)
    .setUserId(sender.id)
    .seRole(sender.role)
    .build();
  return user;
}

const state = {
  chatId: "",
  sender: {},
  messages: [],
  timeoutId: null,
};

const getters = {
  getMessages(state) {
    return state.messages;
  },
  hasUnreadMessages(state) {
    const hasUnread = state.messages.some(
      (message) => !message.isRead && !message.isCurrentUser
    );
    return hasUnread;
  },
  getCurrentStudentLessonId(state, getters, rootState, rootGetters) {
    const isStudent = rootGetters["UserStore/isStudent"];
    const studentLessonId = isStudent
      ? rootGetters["CurrentLessonStore/getCurrentStudentLessonId"]
      : rootGetters["TeacherRoomStore/getCurrentStudentLessonId"];
    return studentLessonId;
  },
};
const actions = {
  startActualizeChat({ state, commit, dispatch }) {
    const timeoutId = setTimeout(async function () {
      await dispatch("initChat", {
        chatId: state.chatId,
      });
      dispatch("startActualizeChat");
    }, 1 * 60 * 1000);
    commit("setTimeoutId", timeoutId);
  },
  stopActualizeChat({ state, commit }) {
    if (!state.timeoutId) {
      return;
    }
    clearTimeout(state.timeoutId);

    commit("setTimeoutId", null);
  },

  async sendMessages({ state, commit, dispatch, getters }, payload) {
    const { from, to } = payload;
    const studentLessonId = getters.getCurrentStudentLessonId;
    await MessageService.sendMessage(
      state.chatId,
      from.text,
      from.file,
      studentLessonId
    );
    await dispatch("initChat", {
      chatId: state.chatId,
    });
  },
  async createChat({}, payload) {
    const { studentLessonId } = payload;
    return MessageService.createChart(studentLessonId);
  },
  async initChat({ commit, rootGetters }, payload) {
    const { chatId } = payload;
    const { data } = await MessageService.getChartMessages(chatId);
    const { messages } = data;
    const messageBuilder = new MessageBuilder();
    const chatMessages = [];
    let sender;
    commit("setChatId", chatId);
    for (let i = 0; i < messages.length; i++) {
      const rawMessage = messages[i];
      const user = createUser(rawMessage.sender);
      const message = messageBuilder
        .setId(rawMessage.id)
        .setText(rawMessage.text)
        .setUserView(createUser(rawMessage.sender))
        .setDate(new Date(rawMessage.sendtime))
        .setIsCurrentUser(rawMessage.is_current_user)
        .setFiles(rawMessage.files)
        .setIsRead(rawMessage.is_read)
        .build();
      if (rawMessage.is_current_user) {
        sender = user;
      }
      chatMessages.push(message);
    }
    chatMessages.sort((messA, messB) => {
      return messA.date.getTime() - messB.date.getTime();
    });
    if (!sender) {
      sender = rootGetters["UserStore/getUser"];
    }

    commit("setMessages", chatMessages);
    commit("setSender", sender);
  },
  async removeMessageById({ commit }, payload) {
    const { messageId } = payload;
    const { data } = await MessageService.removeMessageById(messageId);
    commit("removeMessageById", messageId);
  },

  async markAsRead({ commit, getters }, payload) {
    const { ids } = payload;
    const studentLessonId = getters.getCurrentStudentLessonId;
    await MessageService.markAsRead(ids, studentLessonId);
    commit("markAsRead", ids);
  },
};

const mutations = {
  addNewMessage(state, message) {
    state.messages.push(message);
  },
  setMessages(state, messages) {
    state.messages = messages;
  },
  removeMessageById(state, messageId) {
    const index = state.messages.findIndex((msg) => {
      return msg.id === messageId;
    });
    if (index === -1) {
      return;
    }
    state.messages.splice(index, 1);
  },
  setChatId(state, chatId) {
    state.chatId = chatId;
  },
  setSender(state, sender) {
    state.sender = sender;
  },
  setTimeoutId(state, timeoutId) {
    state.timeoutId = timeoutId;
  },
  markAsRead(state, ids) {
    state.messages.forEach((message) => {
      if (ids.includes(message.id)) {
        message.isRead = true;
      }
    });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
